/**
 * Equipment / zone Tietopohja IA — presentation types + helpers.
 *
 * Type-agnostic shapes for Signaalit + Kyvykkyydet and the three lenses.
 * Live content is built by per-type adapters (e.g. `equipment-kb/air-handling-unit.ts`).
 * Finnish labels here are display chrome only — never filter keys.
 */

import type {
  KBBehavior,
  KBBehaviorDef,
  KBCriterionText,
  KBEquipmentType,
  KBQuantityFamily,
} from "@/lib/api/agent";
import { criterionSourceLabels } from "@/lib/kb-provenance";

export type KbCapabilityLayer = "monitoring" | "potential" | "optimization";

/** Facet inside a physical air-path Virta. */
export type KbRuleFacet = "temperature" | "pressure" | "power";

/** Finnish UI labels for rule facets (copy only). */
export const KB_FACET_LABEL_FI: Record<KbRuleFacet, string> = {
  temperature: "Lämpö",
  pressure: "Paine",
  power: "Teho",
};

export type KbBehaviorPlacement =
  | {
      kind: "under-component";
      /** One or more component homes (multi = cross-cutting shared). */
      underIds: string[];
      layer: KbCapabilityLayer;
      /** Sibling behavior id for paired supply/extract instances. */
      pairId?: string;
    }
  | {
      kind: "under-chain";
      underIds: string[];
      layer: KbCapabilityLayer;
      pairId?: string;
    }
  | { kind: "unit"; layer: KbCapabilityLayer }
  | { kind: "hide-as-wrapper" };

export const KB_LAYER_LABEL_FI: Record<KbCapabilityLayer, string> = {
  monitoring: "seuranta",
  potential: "potentiaali",
  optimization: "optimointi",
};

/** Section headers on Komponentit / Virta (peer to Signaalit · Laskennat). */
export const KB_LAYER_SECTION_LABEL_FI: Record<KbCapabilityLayer, string> = {
  monitoring: "Seuranta",
  potential: "Potentiaali",
  optimization: "Optimointi",
};

export const KB_CAPABILITY_LAYER_ORDER: KbCapabilityLayer[] = [
  "monitoring",
  "potential",
  "optimization",
];

/** Live YAML name_fi for a component or analysis id — not a frontend dictionary. */
export function kbEntityNameFi(kb: KBEquipmentType, id: string): string {
  const component = kb.components.find((c) => c.id === id);
  if (component?.name_fi) return component.name_fi;
  const analysis = (kb.analyses ?? []).find((a) => a.id === id);
  if (analysis?.name_fi) return analysis.name_fi;
  const connection = (kb.connection_analyses ?? []).find((a) => a.id === id);
  if (connection?.name_fi) return connection.name_fi;
  return id;
}

/** Live YAML name_fi for a data-point id (component-owned or shared). */
export function kbDataPointNameFi(
  kb: KBEquipmentType,
  id: string,
): string | undefined {
  for (const component of kb.components) {
    const dp = component.data_points.find((point) => point.id === id);
    if (dp?.name_fi) return dp.name_fi;
  }
  return kb.shared_data_points?.find((point) => point.id === id)?.name_fi;
}

/** Non-empty layer buckets in display order. */
export function groupByCapabilityLayer<T>(
  items: T[],
  layerOf: (item: T) => KbCapabilityLayer,
): { layer: KbCapabilityLayer; items: T[] }[] {
  return KB_CAPABILITY_LAYER_ORDER.map((layer) => ({
    layer,
    items: items.filter((item) => layerOf(item) === layer),
  })).filter((group) => group.items.length > 0);
}

export interface KbChainStep {
  label_fi: string;
  /**
   * When set with `data_point`, this step is an instrumented temperature-chain
   * landmark used to derive component order for path interface joins.
   * `"equipment"` = pipe-end / shared landmark.
   */
  component?: string | "equipment";
  data_point?: string;
}

/**
 * Path / dependency capability row (draft catalog).
 *
 * Same kind as a component-local kyvykkyys — layer, findings, optional points —
 * but scope spans more than one signal/part (or a boundary).
 *
 * **Operator text** (preferred — matches live KB criterion fields):
 *   title (`name_fi`) = komento → odotettu muutos
 *   observation / explanation / remedy = what / why / what to do
 *
 * Legacy `question_fi` + `insight_fi` remain until a row is retoned; the UI
 * prefers the three-role fields when present.
 */
export interface KbProposedChainRule {
  id: string;
  name_fi: string;
  /**
   * @deprecated Prefer observation_fi. Kept as fallback for unmigrated rows
   * (was “diagnostic question” authoring aid, not operator copy).
   */
  question_fi?: string;
  compares: string[];
  /**
   * @deprecated Prefer explanation_fi + remedy_fi. Cleared when three-role
   * prose is present (was mixed author notes / KB ids).
   */
  insight_fi?: string;
  /** What happened — measured fact only, plain language. */
  observation_fi?: string;
  /** Why it may be happening — cause hypothesis, not a restatement. */
  explanation_fi?: string;
  /** What the operator should do — concrete steps. */
  remedy_fi?: string;
  layer: KbCapabilityLayer;
  status: "in-kb" | "connection" | "proposed";
  /** Temperature / pressure facet inside an air-path chain (omit on zone / unit). */
  facet?: KbRuleFacet;
  /**
   * Owning component ids (language-neutral). Used for part-filter / Komponentti
   * projection — never Finnish labels.
   */
  component_ids?: string[];
  /**
   * Display-only chip labels (locale). Must not drive filtering or placement.
   * Prefer deriving from `component_ids` + live component `name_fi`.
   */
  component_labels_fi?: string[];
  /** Labelled sources behind this rule (REQ-AGT-28); empty when it cites nothing. */
  source_labels?: string[];
}

export interface KbPathChain {
  id: string;
  name_fi: string;
  description_fi: string;
  steps: KbChainStep[];
  source: "kb" | "spike-gap" | "zone-link";
  proposed_rules: KbProposedChainRule[];
  /**
   * Quantity families whose effective / minted channel appears on this path's
   * anatomy steps (e.g. tehoketju stage powers). Shown as Laskennat cards.
   */
  calc_families?: KbPathCalcFamily[];
}

/** A quantity family bound to a path step's owning component. */
export interface KbPathCalcFamily {
  component_id: string;
  family: KBQuantityFamily;
}

/** Whether a family is the quantity named (or minted sibling of) a chain data_point. */
export function quantityFamilyMatchesStepDataPoint(
  family: KBQuantityFamily,
  dataPoint: string,
): boolean {
  if (family.id === dataPoint) return true;
  if (family.estimators.some((e) => e.data_point_id === dataPoint)) return true;
  return dataPoint.startsWith(`${family.id}__`);
}

/**
 * Whether a capability (component criterion or component-home behavior) reads a chain step's
 * `data_point` — the dual-lens facet-routing rule (story #1235). A capability's owning part
 * sitting on a Virta's chain is not enough on its own: `heat-recovery-unit` sits on both
 * `extract_temperature_chain` and `extract_pressure_chain`, so its temperature criteria must
 * not flood the pressure Virta. Route by referenced signal instead — the same rule
 * `required_data_points[].id` already applies to `home: path` behavior nesting, applied here
 * uniformly to `home: component` capabilities too.
 */
export function capabilitySignalsChainStep(
  requiredDataPointIds: string[] | undefined,
  dataPoint: string,
): boolean {
  return (requiredDataPointIds ?? []).includes(dataPoint);
}

export interface KbBoundarySignal {
  id: string;
  name_fi: string;
  side: "self" | "peer";
  unit?: string;
}

export interface KbDependencyGroup {
  id: string;
  /** Live-style counterpart key (zone type, equipment type, or building). */
  counterpart_type: string;
  name_fi: string;
  description_fi: string;
  /** Boundary inventory — not “who needs what” (that’s on capabilities). */
  signals: KbBoundarySignal[];
  rules: KbProposedChainRule[];
}

/**
 * Placement from live behavior payload (`home` / `layer` / `under_ids` / `pair_id`).
 * Returns undefined when `home` is omitted — YAML must author placement.
 */
export function placementFromBehavior(
  behavior: Pick<
    KBBehaviorDef,
    "id" | "home" | "layer" | "under_ids" | "pair_id"
  >,
): KbBehaviorPlacement | undefined {
  const layer = (behavior.layer ?? "monitoring") as KbCapabilityLayer;
  const pairId = behavior.pair_id ?? undefined;
  if (behavior.home === "unit") {
    return { kind: "unit", layer };
  }
  if (behavior.home === "path") {
    return {
      kind: "under-chain",
      underIds: behavior.under_ids ?? [],
      layer,
      pairId,
    };
  }
  if (behavior.home === "component" || behavior.home === "dependency") {
    return {
      kind: "under-component",
      underIds: behavior.under_ids ?? [],
      layer,
      pairId,
    };
  }
  return undefined;
}

/** Capability title — catalog/live `name_fi` (display only). */
export function resolveRuleTitleFi(
  rule: Pick<KbProposedChainRule, "id" | "name_fi">,
): string {
  return rule.name_fi;
}

/**
 * Instance-scope readiness for a capability card — overlaid on the shared Tietopohja card
 * contract so Kyvykkyydet reuses one card schema instead of forking a second instance card.
 * Mirrors the Kyvykkyydet status vocabulary: an applicable
 * capability reads as active, blocked by a missing signal, or blocked by a missing parameter;
 * `learning` is the passive nuance of a parameter block (a learned parameter still accumulating
 * samples, vs. a config value a human must set). `finding` / `no_data` cover the rarer
 * `fail` / `no_data` behaviour statuses so the mapper stays total over `KBBehaviorStatus`.
 */
export type KbCapabilityReadiness =
  | "active"
  | "needs_signal"
  | "needs_parameter"
  | "learning"
  | "finding"
  | "no_data";

export const KB_CAPABILITY_READINESS_LABEL_FI: Record<
  KbCapabilityReadiness,
  string
> = {
  active: "aktiivinen",
  needs_signal: "vaatii signaalia",
  needs_parameter: "vaatii parametria",
  learning: "oppii",
  finding: "havainto",
  no_data: "ei dataa",
};

/**
 * Pure status → readiness mapping (no React, no fetch) — the source for the instance-readiness
 * pill on `KbCapabilityCard`. Reads `partially_ready` too: buckets a
 * partially-ready behaviour ("some but not all criteria blocked") together with `unready` under
 * "vaatii parametria", and `unready_reason` is populated by the backend whenever `partially_ready`
 * is true — even when the coarse `status` is still `active`
 * (services/agent/.../knowledge_base/behavior_status.py) — so a partially-ready-while-learning
 * behaviour still reads as the passive "learning" nuance rather than a plain "active".
 * `unwired` stays the primary blocked state regardless of `partially_ready` (a missing signal, not
 * a missing parameter). `finding` / `no_data` cover the rarer `fail` / `no_data` statuses so the
 * mapper stays total over `KBBehaviorStatus`.
 */
export function readinessFromBehaviorStatus(
  behavior: Pick<KBBehavior, "status" | "unready_reason" | "partially_ready">,
): KbCapabilityReadiness {
  if (behavior.status === "unwired") return "needs_signal";
  if (behavior.status === "unready" || behavior.partially_ready) {
    return behavior.unready_reason === "learning"
      ? "learning"
      : "needs_parameter";
  }
  if (behavior.status === "active") return "active";
  if (behavior.status === "fail") return "finding";
  return "no_data";
}

export interface KbCapabilityCardModel {
  id: string;
  title_fi: string;
  layer: KbCapabilityLayer;
  home: KbCapabilityHome;
  observation_fi?: string;
  explanation_fi?: string;
  remedy_fi?: string;
  /** Temporary fallback when draft voice / catalog three-role is missing. */
  description_fi?: string;
  legacy_question_fi?: string;
  legacy_insight_fi?: string;
  signals: KbRequiredSignalAnnotation[];
  points: number | null;
  status?: "in-kb" | "proposed" | "connection";
  part_labels_fi?: string[];
  shared_labels_fi?: string[];
  pair_label_fi?: string | null;
  /** Default: show part chips only for path / dependency homes. */
  show_part_labels?: boolean;
  /**
   * Instance-scope readiness overlay (Kyvykkyydet only). Omitted for the Tietopohja type view,
   * which renders exactly as before when this field is absent.
   */
  readiness?: KbCapabilityReadiness;
  /**
   * The published sources behind this rule, already labelled (REQ-AGT-28). Empty or absent for
   * a rule that cites nothing — the card then names no standard rather than inventing one.
   */
  source_labels?: string[];
}

export function capabilityCardFromRule(
  rule: KbProposedChainRule,
  behaviorById: Map<string, KBBehaviorDef>,
  home: KbCapabilityHome,
): KbCapabilityCardModel {
  const live = behaviorById.get(rule.id);
  const ids = requiredSignalIdsForCapability(rule.compares, live);
  return {
    id: rule.id,
    title_fi: resolveRuleTitleFi(rule),
    layer: rule.layer,
    home,
    observation_fi: rule.observation_fi,
    explanation_fi: rule.explanation_fi,
    remedy_fi: rule.remedy_fi,
    legacy_question_fi: rule.question_fi,
    legacy_insight_fi: rule.insight_fi || undefined,
    signals: annotateRequiredSignals(ids),
    points:
      live && live.full_potential_weight > 0
        ? live.full_potential_weight
        : null,
    status: rule.status,
    part_labels_fi: rule.component_labels_fi,
    show_part_labels: home === "path" || home === "dependency",
    source_labels: rule.source_labels,
  };
}

export function capabilityCardFromBehavior(
  behavior: KBBehaviorDef,
  layer: KbCapabilityLayer,
  home: KbCapabilityHome,
  opts?: {
    shared_labels_fi?: string[];
    pair_label_fi?: string | null;
    criteriaText?: Record<string, KBCriterionText>;
  },
): KbCapabilityCardModel {
  const primary = behavior.operational_criteria[0];
  const text =
    primary && opts?.criteriaText ? opts.criteriaText[primary.id] : undefined;
  const ids = requiredSignalIdsForCapability([], behavior);
  return {
    id: behavior.id,
    title_fi: behavior.name_fi,
    layer,
    home,
    observation_fi: text?.observation_fi,
    explanation_fi: text?.explanation_fi ?? undefined,
    remedy_fi: text?.remedy_fi ?? undefined,
    description_fi: text ? undefined : behavior.description_fi || undefined,
    signals: annotateRequiredSignals(ids),
    points:
      behavior.full_potential_weight > 0
        ? behavior.full_potential_weight
        : null,
    status: "in-kb",
    shared_labels_fi: opts?.shared_labels_fi,
    pair_label_fi: opts?.pair_label_fi ?? null,
    show_part_labels: false,
    // A behaviour card shows the sources of the rule it leads with — the same criterion whose
    // three-role prose it already renders.
    source_labels: criterionSourceLabels(primary?.claims),
  };
}

/**
 * Quantity-family calculation as a capability-shaped card (same schema; no new object type).
 * Backed by `components[].quantity_families` — not a `behaviors/*.yaml` package.
 */
export function capabilityCardFromQuantityFamily(
  family: KBQuantityFamily,
): KbCapabilityCardModel {
  const byId = new Map(family.estimators.map((e) => [e.id, e]));
  const priorityLabels = family.priority
    .map((id) => byId.get(id))
    .filter((e): e is NonNullable<typeof e> => e != null)
    .map((e) => {
      if (e.kind === "measured") return "mitattu";
      if (e.kind === "accepted") return "hyväksytty";
      return e.name_fi || "laskettu";
    });

  const derived = family.estimators.filter((e) => e.kind === "derived");
  const formulaLines = derived
    .filter((e) => e.expression)
    .map((e) => `${e.id}: ${e.expression}`);

  const signals: KbRequiredSignalAnnotation[] = [];
  const seen = new Set<string>();
  for (const est of family.estimators) {
    if (est.kind === "measured" && !seen.has(est.data_point_id)) {
      seen.add(est.data_point_id);
      signals.push({ id: est.data_point_id, source_fi: "mitattu" });
    }
    for (const cfg of est.requires_config ?? []) {
      if (!seen.has(cfg)) {
        seen.add(cfg);
        signals.push({ id: cfg, source_fi: "parametri" });
      }
    }
    for (const lp of est.requires_learned ?? []) {
      if (!seen.has(lp)) {
        seen.add(lp);
        signals.push({ id: lp, source_fi: "opittu" });
      }
    }
  }
  // Inputs named only in derived expressions (e.g. supply_duct_pressure) — show as mittaukset
  // when they aren't the family's measured channel.
  for (const est of derived) {
    const expr = est.expression ?? "";
    for (const token of expr.match(/\b[a-z][a-z0-9_]*\b/gi) ?? []) {
      const t = token.toLowerCase();
      if (
        t === "air_rho_cp" ||
        t === "and" ||
        t === "or" ||
        t === "abs" ||
        t === "on" ||
        t === "off" ||
        t === "true" ||
        t === "false"
      ) {
        continue;
      }
      if (seen.has(token) || byId.has(token)) continue;
      if (!token.includes("_")) continue; // skip bare words; keep canonical ids
      seen.add(token);
      signals.push({ id: token, source_fi: "lauseke" });
    }
  }

  const unit = family.unit ? ` (${family.unit})` : "";
  const title = family.name_fi || family.name_en || `Tehollinen ${family.id}`;

  return {
    id: `quantity_family:${family.id}`,
    title_fi: `${title}${unit}`,
    layer: "monitoring",
    home: "component",
    description_fi: [
      `Tehollinen arvo (kyvykkyydet käyttävät): ${priorityLabels.join(" → ") || family.priority.join(" → ")}.`,
      formulaLines.length > 0 ? `Laskenta: ${formulaLines.join(" · ")}` : null,
      `Quantity family \`${family.id}\` — taustakanavat säilyvät; mitattua ei ylikirjoiteta.`,
    ]
      .filter(Boolean)
      .join(" "),
    observation_fi:
      derived.length > 0
        ? "Laskettu estimaattori on valmis kun mittaukset ja parametrit (alla) täyttyvät; muuten tehollinen on mitattu jos se on kytketty."
        : undefined,
    remedy_fi: derived.some((e) => (e.requires_config ?? []).length > 0)
      ? "Anna vaaditut parametrit (esim. fan_k) ja kytke lausekkeen signaalit, jotta laskettu estimaattori avautuu."
      : undefined,
    signals,
    points: null,
    status: "in-kb",
    show_part_labels: false,
  };
}

/** Deduped cross-part capabilities for one component (Komponentti lens). */
export function proposedRulesForComponent(
  componentId: string,
  chains: KbPathChain[],
): KbProposedChainRule[] {
  const byId = new Map<string, KbProposedChainRule>();
  for (const chain of chains) {
    for (const rule of chain.proposed_rules) {
      if (ruleBelongsToComponent(rule, componentId)) {
        byId.set(rule.id, rule);
      }
    }
  }
  return [...byId.values()];
}

/**
 * Equipment-level signals for Laitteen taso — same kind as component ports,
 * one scale up. Zone / DH substitutes stay under Riippuvuudet.
 */
export type KbUnitSignalGroup =
  "outdoor" | "supply" | "extract" | "setpoint" | "flow" | "pressure";

export const KB_UNIT_SIGNAL_GROUP_LABEL_FI: Record<KbUnitSignalGroup, string> =
  {
    outdoor: "Ulkoilma",
    supply: "Tuloilmakanava · lämpötilat",
    extract: "Poistoilmakanava · lämpötilat",
    setpoint: "Asetusarvot",
    flow: "Ilmavirrat",
    pressure: "Paineet (koneen taso)",
  };

export interface KbUnitLevelSignal {
  id: string;
  name_fi: string;
  unit: string;
  criticality: "required" | "recommended" | "optional";
  group: KbUnitSignalGroup;
  /** Live KB home — shared-sensors or a component id. */
  owner: "shared" | string;
  note_fi?: string;
}

export const KB_UNIT_SIGNAL_GROUP_ORDER: KbUnitSignalGroup[] = [
  "outdoor",
  "supply",
  "extract",
  "setpoint",
  "flow",
  "pressure",
];

export function resolveUnitSignalNameFi(
  signal: KbUnitLevelSignal,
  components: { id: string; data_points: { id: string; name_fi: string }[] }[],
): string {
  for (const c of components) {
    const hit = c.data_points.find((dp) => dp.id === signal.id);
    if (hit?.name_fi) return hit.name_fi;
  }
  return signal.name_fi;
}

/**
 * Shared part focus for flow + component lenses (`all` / `unit` / component id).
 */
export type KbPartFocus = "all" | "unit" | (string & {});

/** Path chains that include this part (empty for unit-level focus). */
export function chainsForPartFocus(
  focus: KbPartFocus,
  chains: KbPathChain[],
  airPaths: Record<string, string[]>,
): KbPathChain[] {
  if (focus === "all") return chains;
  if (focus === "unit") return [];
  const pathIds = new Set(airPaths[focus] ?? []);
  return chains.filter((c) => pathIds.has(c.id));
}

/**
 * Whether a path/dependency capability belongs to a component for filtering.
 * Uses language-neutral `component_ids` only.
 */
export function ruleBelongsToComponent(
  rule: KbProposedChainRule,
  componentId: string,
): boolean {
  return (rule.component_ids ?? []).includes(componentId);
}

/** Narrow relationship rules inside a path chain when a part is selected. */
export function filterChainRulesForPart(
  rules: KbProposedChainRule[],
  focus: KbPartFocus,
): KbProposedChainRule[] {
  if (focus === "all" || focus === "unit") return rules;
  return rules.filter((r) => ruleBelongsToComponent(r, focus));
}

/** Narrow path calc cards when a part is selected. */
export function filterChainCalcFamiliesForPart(
  calcs: KbPathCalcFamily[],
  focus: KbPartFocus,
): KbPathCalcFamily[] {
  if (focus === "all" || focus === "unit") return calcs;
  return calcs.filter((c) => c.component_id === focus);
}

/** Component filter: keep chains that still have rules or calc cards for the part. */
export function pathChainHasPartContent(
  chain: KbPathChain,
  focus: KbPartFocus,
): boolean {
  if (focus === "all" || focus === "unit") return true;
  return (
    filterChainRulesForPart(chain.proposed_rules, focus).length > 0 ||
    filterChainCalcFamiliesForPart(chain.calc_families ?? [], focus).length > 0
  );
}

/** Optional path-interface vocabulary (before/after on a stream). Type adapters supply ports. */
export type KbInterfaceFace = "before" | "after";
export type KbInterfaceStream = "supply" | "extract";
export type KbInterfacePeerScope = "component" | "equipment" | "zone";

export const KB_INTERFACE_PEER_SCOPE_LABEL_FI: Record<
  KbInterfacePeerScope,
  string
> = {
  component: "komponentti",
  equipment: "laite",
  zone: "vyöhyke",
};

export interface KbComponentInterfacePort {
  id: string;
  name_fi: string;
  face: KbInterfaceFace;
  stream: KbInterfaceStream;
}

export interface KbPathInterfaceJoin {
  peer_scope: "component" | "equipment";
  peer_id: string;
  peer_signal_id: string;
  peer_name_fi: string;
  source: "order";
}

/**
 * Normalized inventory signal row for every dossier that lists Signaalit.
 * Komponentit · Laitteen taso · Riippuvuudet — one schema → one row component.
 * Vaikutusketjut keeps path steps (anatomy), not this inventory list.
 */
export interface KbSignalRowModel {
  id: string;
  name_fi: string;
  unit?: string;
  criticality?: "required" | "recommended" | "optional";
  /**
   * Id chip styling: interface ports are muted; owned uses criticality;
   * boundary defaults to neutral.
   */
  emphasis: "owned" | "interface" | "neutral";
  /** Optional chip (owner, boundary side, or ↔ join). */
  meta_fi?: string;
  meta_title?: string;
  note_fi?: string;
}

/** One row in the unified Signaalit list on a component dossier. */
export interface KbDossierSignalRow {
  id: string;
  name_fi: string;
  kind: "owned" | "interface";
  unit?: string;
  criticality?: "required" | "recommended" | "optional";
  join?: KbPathInterfaceJoin;
  /** Optional chip (e.g. laskettu) — quantity-family derived channels. */
  meta_fi?: string;
  meta_title?: string;
  note_fi?: string;
}

/**
 * Single signal list: optional interface ports + remaining owned data points.
 * Pass `ports` from a type-specific path topology (empty = owned inventory only).
 */
export function dossierSignalsForComponent(
  componentId: string,
  owned: { id: string; name_fi: string; unit?: string; criticality?: string }[],
  ports: KbComponentInterfacePort[] = [],
  joinForPort?: (
    componentId: string,
    port: KbComponentInterfacePort,
  ) => KbPathInterfaceJoin | null,
): KbDossierSignalRow[] {
  const portIds = new Set(ports.map((p) => p.id));
  const rows: KbDossierSignalRow[] = ports.map((port) => ({
    id: port.id,
    name_fi: port.name_fi,
    kind: "interface" as const,
    join: joinForPort?.(componentId, port) ?? undefined,
  }));

  for (const dp of owned) {
    if (portIds.has(dp.id)) continue;
    rows.push({
      id: dp.id,
      name_fi: dp.name_fi,
      kind: "owned",
      unit: dp.unit,
      criticality: dp.criticality as KbDossierSignalRow["criticality"],
    });
  }
  return rows;
}

/** Fallback short label for chips when live `name_fi` is unavailable. */
export function componentShortLabelFi(
  componentId: string,
  labels: Record<string, string> = {},
): string {
  return labels[componentId] ?? componentId;
}

export function signalRowFromDossier(
  row: KbDossierSignalRow,
): KbSignalRowModel {
  let meta_fi: string | undefined = row.meta_fi;
  let meta_title: string | undefined = row.meta_title;
  if (!meta_fi && row.join) {
    const peerLabel =
      row.join.peer_scope === "component"
        ? componentShortLabelFi(row.join.peer_id)
        : row.join.peer_scope === "equipment"
          ? "laite"
          : null;
    if (peerLabel) {
      meta_fi = `↔ ${KB_INTERFACE_PEER_SCOPE_LABEL_FI[row.join.peer_scope]} · ${peerLabel} · ${row.join.peer_signal_id}`;
      meta_title = "Yhdistetty polun järjestyksestä (sama lukema)";
    }
  }
  return {
    id: row.id,
    name_fi: row.name_fi,
    unit: row.unit,
    criticality: row.criticality,
    emphasis: row.kind === "interface" ? "interface" : "owned",
    meta_fi,
    meta_title,
    note_fi: row.note_fi,
  };
}

export function signalRowFromUnit(
  signal: KbUnitLevelSignal,
  nameFi: string,
): KbSignalRowModel {
  return {
    id: signal.id,
    name_fi: nameFi,
    unit: signal.unit || undefined,
    criticality: signal.criticality,
    emphasis: "owned",
    meta_fi: signal.owner === "shared" ? "laitteen taso" : signal.owner,
    note_fi: signal.note_fi,
  };
}

const KB_BOUNDARY_SIDE_LABEL_FI: Record<KbBoundarySignal["side"], string> = {
  self: "laite",
  peer: "vastapuoli",
};

export function signalRowFromBoundary(
  signal: KbBoundarySignal,
): KbSignalRowModel {
  return {
    id: signal.id,
    name_fi: signal.name_fi,
    unit: signal.unit,
    emphasis: "neutral",
    meta_fi: KB_BOUNDARY_SIDE_LABEL_FI[signal.side],
  };
}

/** Where a capability lives in the IA (not a separate object type). */
export type KbCapabilityHome = "component" | "path" | "unit" | "dependency";

export const KB_CAPABILITY_HOME_LABEL_FI: Record<KbCapabilityHome, string> = {
  component: "komponentti",
  path: "polku",
  unit: "laite",
  dependency: "riippuvuus",
};

/**
 * Strip parenthetical / prose notes from a compares token toward a signal id.
 * Keeps dotted ids (`zone.temperature`) and arrow leftovers are dropped.
 */
export function normalizeCompareToken(token: string): string {
  return token
    .replace(/\s*\(.*\)\s*$/, "")
    .replace(/\s*→.*$/, "")
    .replace(/\s+tai\s+.+$/i, "")
    .trim();
}

/**
 * Required signals for a capability: prefer live KB `required_data_point_ids`,
 * else draft `compares` tokens (normalized).
 */
export function requiredSignalIdsForCapability(
  compares: string[],
  behavior?: Pick<KBBehaviorDef, "required_data_point_ids"> | null,
): string[] {
  if (behavior?.required_data_point_ids?.length) {
    return [...behavior.required_data_point_ids];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of compares) {
    const id = normalizeCompareToken(raw);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export interface KbRequiredSignalAnnotation {
  id: string;
  /** Finnish source hint — component label, "laitteen taso", or "vyöhyke". */
  source_fi: string | null;
}

/** Shared / unit-level signal ids — source hint "laitteen taso". */
const KB_SHARED_UNIT_SIGNAL_IDS = new Set([
  "outdoor_air_temp",
  "supply_air_temp",
  "extract_air_temp",
  "exhaust_air_temp",
  "supply_air_temp_setpoint",
  "extract_air_temp_setpoint",
  "supply_air_flow",
  "extract_air_flow",
  "supply_duct_pressure",
  "extract_duct_pressure",
  "fresh_air_temp",
]);

/**
 * Resolve inventory home for a required signal id.
 * Type-specific owners/ports may refine this via optional `lookup`.
 */
export function annotateRequiredSignal(
  id: string,
  lookup?: {
    componentLabel?: (componentId: string) => string;
    ownerBySignalId?: Record<string, string>;
    portsByComponent?: Record<string, { id: string }[]>;
  },
): KbRequiredSignalAnnotation {
  if (KB_SHARED_UNIT_SIGNAL_IDS.has(id)) {
    return { id, source_fi: "laitteen taso" };
  }
  if (lookup?.portsByComponent) {
    const label = lookup.componentLabel ?? ((c: string) => c);
    for (const [compId, ports] of Object.entries(lookup.portsByComponent)) {
      if (ports.some((p) => p.id === id)) {
        return { id, source_fi: label(compId) };
      }
    }
  }
  const owner = lookup?.ownerBySignalId?.[id];
  if (owner) {
    const label = lookup.componentLabel ?? ((c: string) => c);
    return { id, source_fi: label(owner) };
  }
  if (
    id.startsWith("zone.") ||
    id.startsWith("zone ") ||
    id.startsWith("ahu ")
  ) {
    return { id, source_fi: "vyöhyke / raja" };
  }
  return { id, source_fi: null };
}

export function annotateRequiredSignals(
  ids: string[],
  lookup?: Parameters<typeof annotateRequiredSignal>[1],
): KbRequiredSignalAnnotation[] {
  return ids.map((id) => annotateRequiredSignal(id, lookup));
}
